import type { Language } from '@seek/ca-myprofile-utils';
import { createForm } from '@seek/forms-ui';
import { useLanguage } from '@vocab/react';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { useApiClient } from 'src/modules/ApiProvider';
import type { ChangePasswordFailure } from 'src/modules/graphql/types';
import showCustomerServiceView from 'src/modules/sso/showCustomerServiceView';
import type { RootState } from 'src/reducers/index';
import { PageUpdateActionType } from 'src/reducers/page/actionCreators';
import type { ChangePasswordModel } from 'src/reducers/user/actionCreators';
import type { ErrorMessage } from 'src/types';

import CustomerServiceForm from './CustomerServiceForm';
import UserForm from './UserForm';

const FORM_NAME = 'changePassword';

const mapStateToProps = (state: RootState) => ({
  emailAddress: state.user.emailAddress,
});

const { FormProvider, useField } = createForm<ChangePasswordModel>(FORM_NAME, {
  formType: 'braid',
});

export type PasswordUseField = typeof useField;

const ChangePasswordForm = ({
  toggle,
  emailAddress,
  setSuccess,
}: {
  toggle: () => void;
  emailAddress: string;
  setSuccess: (state: boolean) => void;
}) => {
  const [customerServiceView, setCustomerServiceView] = React.useState(false);
  const [error, setError] = useState<ErrorMessage>();
  useEffect(() => {
    showCustomerServiceView().then(setCustomerServiceView);
  }, [setCustomerServiceView]);
  const [submitting, setSubmitting] = React.useState(false);

  const { language } = useLanguage();
  const dispatch = useDispatch();
  const api = useApiClient();

  const onSubmit = (formValues: ChangePasswordModel) => {
    setSubmitting(true);

    api
      .changePassword(formValues, emailAddress)
      .then((result: any) => {
        setSubmitting(false);
        if (result?.__typename === 'ChangePasswordFailure') {
          const failure = result as ChangePasswordFailure;

          if (failure.error.__typename === 'AccountValidationError') {
            setError(
              failure.error?.details[0]?.message || failure.error.message,
            );
            return;
          }
          setError(failure.error.message);
          return;
        }

        dispatch({
          type: PageUpdateActionType.SET_CHANGE_PASSWORD_SUCCESS,
        });

        toggle();
        setSuccess(true);
      })
      .catch((err: any) => {
        setError(err?.message || 'Oops!');
        setSubmitting(false);
      });
  };

  return (
    <FormProvider
      initialValues={{
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }}
      language={language as Language}
    >
      {customerServiceView
        ? ({ handleSubmit, setFieldValue }) => {
            const setPasswordFields = (value: string) => {
              setFieldValue('newPassword', value);
              setFieldValue('confirmPassword', value);
            };
            return (
              <CustomerServiceForm
                onSubmit={handleSubmit(onSubmit)}
                error={error}
                toggle={toggle}
                useField={useField}
                setPasswordFields={setPasswordFields}
                submitting={submitting}
              />
            );
          }
        : ({ handleSubmit }) => (
            <UserForm
              onSubmit={handleSubmit(onSubmit)}
              error={error}
              toggle={toggle}
              useField={useField}
              emailAddress={emailAddress}
              submitting={submitting}
            />
          )}
    </FormProvider>
  );
};

export default connect(mapStateToProps, {})(ChangePasswordForm);
